import React, { Component } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import moment from 'moment';
import upperFirst from 'lodash/upperFirst';
import findIndex from 'lodash/findIndex';
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import * as XLSX from 'xlsx';

import { getUserFullName } from '../../utils/utilsChat';
import {
  exposeComponentToGlobalScope,
  stringifyTimePeriod,
  withMomentLocale,
  formatNumber,
  inflect,
} from '../../utils/utilsGlobal';

import services, { events } from '../../services/services';
import { getAnalyticsRating } from '../../services/apiAnalytics';
import { Datepicker, Dropdown, Modal, AnalyticsManualSwitchOff } from '../../components';
import uiStyles from '../../styles/ui.scss';
import styles from './AnalyticsRating.scss';


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    currentCompanyOperators: state.currentCompanyOperators,
  };
};

@connect(mapStateToProps, null)
export class AnalyticsRating extends Component {

  OPERATOR_FILTER_DEFAULT_TITLE = 'Все операторы';

  TABLE_CELLS = [
    {
      title: 'Оператор',
      columnName: 'operator',
      valueType: 'text',
      cellFormatting: 'text',
      valueGetter: (row) => getUserFullName(row.employee),
    },
    {
      title: 'Назначено (NST)',
      columnName: 'assignedCasesCount',
      hasSecondaryColumn: true,
      valueType: 'number',
      cellFormatting: 'cases',
    },
    {
      columnName: 'assignedRoomsCount',
      isSecondaryColumnFor: 'assignedCasesCount',
      valueType: 'number',
      cellFormatting: 'rooms',
    },
    {
      title: 'Вручную',
      columnName: 'selfAssignedCasesCount',
      hasSecondaryColumn: true,
      valueType: 'number',
      cellFormatting: 'cases',
    },
    {
      columnName: 'selfAssignedRoomsCount',
      isSecondaryColumnFor: 'selfAssignedCasesCount',
      valueType: 'number',
      cellFormatting: 'rooms',
    },
    {
      title: 'Просрочено,\nкейсов (NTB)',
      columnName: 'overdueCasesCount',
      valueType: 'number',
      cellFormatting: 'cases',
    },
    {
      title: 'Потеряно,\nклиентов (LCR)',
      columnName: 'overdueRoomsCount',
      valueType: 'number',
      cellFormatting: 'rooms',
    },


    {
      title: 'Доля потерянных\nклиентов (LCR)',
      columnName: 'overdueRoomsPercent',
      valueType: 'number',
      cellFormatting: 'percent',
      className: 'percent',
    },
    {
      title: 'Доля нарушения\nвремени реакции (FRTR)',
      columnName: 'overdueCasesPercent',
      valueType: 'number',
      cellFormatting: 'percent',
      className: 'percent',
    },

    {
      title: 'Выкл. себя вручную',
      columnName: 'manualSwitchOffCount',
      hasSecondaryColumn: true,
      valueType: 'number',
      cellFormatting: 'count',
      onClick: (cell, row) => this.onManualSwitchOffClick(cell, row),
    },
    {
      columnName: 'manualSwitchOffDuration',
      isSecondaryColumnFor: 'manualSwitchOffCount',
      valueType: 'duration',
      cellFormatting: 'durationSecondary',
      onClick: (cell, row) => this.onManualSwitchOffClick(cell, row),
    },
    {
      title: 'Сообщений',
      columnName: 'messagesCount',
      valueType: 'number',
      cellFormatting: 'number',
    },
    {
      title: 'Ср. реакция (AFRT)',
      columnName: 'avgReactionTime',
      valueType: 'duration',
      cellFormatting: 'duration',
    },
    {
      title: 'Ср. ответ (AHT)',
      columnName: 'avgResponseTime',
      valueType: 'duration',
      cellFormatting: 'duration',
    },
    {
      title: 'Ср. кейс (ART)',
      columnName: 'avgCaseCloseTime',
      valueType: 'duration',
      cellFormatting: 'duration',
    },
    {
      title: 'Выкл. по просрочке',
      columnName: 'overdueSwitchOffCount',
      hasSecondaryColumn: true,
      valueType: 'number',
      cellFormatting: 'count',
    },
    {
      columnName: 'overdueSwitchOffDuration',
      isSecondaryColumnFor: 'overdueSwitchOffCount',
      valueType: 'duration',
      cellFormatting: 'durationSecondary',
    },
  ];

  state = {
    isLoading: false,
    startDate: null,
    endDate: null,
    selectedOperators: [],
    operatorFilterOptions: [],
    operatorFilterTitle: '',
    operatorFilterSearchQuery: '',
    periodFilterOptions: [],
    periodFilterTitle: '',
    periodFilterModalStartDate: '',
    periodFilterModalEndDate: '',
    isPeriodModalOpen: false,
    reportRatingTable: [],
    reportRatingSortBy: 'assignedCasesCount',
    reportRatingSortingIsAscending: false,
    manualSwitchOffAnalyticsData: null,
  };

  filterOperatorsSearchInput = React.createRef();
  debouncedOnOperatorFilterSearch = debounce(this.onOperatorFilterSearch.bind(this), 400);

  componentDidMount(){
    const operatorFilterTitle = this.OPERATOR_FILTER_DEFAULT_TITLE;
    const periodFilterOptions = this.generatePeriodFilterOptions();
    const periodFilterTitle = periodFilterOptions[0].title;
    const [ startDate, endDate ] = this.parsePeriodFilterValue(periodFilterOptions[0].value);
    const [ periodFilterModalStartDate, periodFilterModalEndDate ] = this.parsePeriodFilterValue(periodFilterOptions[4].value);

    this.setState({
      operatorFilterTitle,
      periodFilterOptions,
      periodFilterTitle,
      periodFilterModalStartDate,
      periodFilterModalEndDate,
      startDate,
      endDate,
    });

    services.userWatcher.on(events.USER_SET_SUCCESS, () => {
      const operatorFilterOptions = this.generateOperatorFilterOptions();
      this.setState({ operatorFilterOptions });
      this.onRatingReportChange(startDate, endDate, []);
    });

    exposeComponentToGlobalScope(this);
  }

  onExportExcelFile = () => {
    const table = document.getElementById('analyticsTable');
    const workbook = XLSX.utils.table_to_book(table, { sheet: 'Аналитика' });
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];
    XLSX.writeFile(workbook, `table_export_${formattedDate}.xlsx`);
  }

  onRatingReportChange = (startDate, endDate, operatorIds) => {
    const { currentCompany } = this.props;
    const { reportRatingSortBy, reportRatingSortingIsAscending } = this.state;
    this.setState({ isLoading: true });

    getAnalyticsRating({ companyId: currentCompany.id, startDate, endDate, operatorIds })
      .then((ratingData) => {
        const reportRatingTable = this.generateRatingTable(ratingData);
        this.setState({
          isLoading: false,
          reportRatingTable: this.getSortedRatingTable(reportRatingTable, reportRatingSortBy, reportRatingSortingIsAscending),
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  };

  generateRatingTableRow = (item, rowType) => {
    item = item || {};
    const { employee } = item;

    const cells = this.TABLE_CELLS.map((cell, index) => {
      if (rowType === 'header') {
        return { ...cell, cellFormatting: 'header', employee };
      }
      else if (rowType === 'summary') {
        const value = !index ? 'Сводка' : item[cell.columnName];
        return { ...cell, value, employee };
      }
      else if (rowType === 'nonWorkingHeader') {
        const cellData = !index
          ? { cellFormatting: 'text', value: 'Не было рабочего времени' }
          : { cellFormatting: 'noDashWhenEmpty' };
        return { ...cell, ...cellData, employee };
      }
      else {
        const value = cell.valueGetter ? cell.valueGetter(item) : item[cell.columnName];
        return { ...cell, value, employee };
      }
    });
    return { rowType, cells };
  };

  generateRatingTable = (ratingData) => {
    let workingOperators = [];
    let nonWorkingOperators = [];

    ratingData.employees.forEach(item => {
      let el = { ...item };
      // Новая колонка! Считается по формуле: Потеряно клиентов (колонка G)/Назначено клиентов (колонка C) *100% = %
      if (el.overdueRoomsCount && el.assignedRoomsCount) {
        el.overdueRoomsPercent = (el.overdueRoomsCount / el.assignedRoomsCount * 100).toFixed(2);
      }
      // Новая колонка! Считается по формуле: Просрочено кейсов (колонка F)/ Назначено назначено кейсов (колонка B) *100% = %
      if (el.overdueCasesCount && el.assignedCasesCount) {
        el.overdueCasesPercent = (el.overdueCasesCount / el.assignedCasesCount * 100).toFixed(2);
      }

      if (item.hasWorkingTime) {
        workingOperators.push(this.generateRatingTableRow(el, null));
      }
      else {
        nonWorkingOperators.push(this.generateRatingTableRow(el, null));
      }
    });

    const summary = ratingData.summary;

    if (summary.overdueRoomsCount && summary.assignedRoomsCount) {
      summary.overdueRoomsPercent = (summary.overdueRoomsCount / summary.assignedRoomsCount * 100).toFixed(2);
    }
    // Новая колонка! Считается по формуле: Просрочено кейсов (колонка F)/ Назначено назначено кейсов (колонка B) *100% = %
    if (summary.overdueCasesCount && summary.assignedCasesCount) {
      summary.overdueCasesPercent = (summary.overdueCasesCount / summary.assignedCasesCount * 100).toFixed(2);
    }

    return [
      this.generateRatingTableRow(null, 'header'),
      ...workingOperators,
      this.generateRatingTableRow(null, 'nonWorkingHeader'),
      ...nonWorkingOperators,
      this.generateRatingTableRow(ratingData.summary, 'summary'),
    ];
  };

  getSortedRatingTable = (table, columnName, isAscending) => {
    const cellSorters = {
      number: (aCell, bCell) => {
        return aCell.value > bCell.value ? -1 : 1;
      },
      text: (aCell, bCell) => {
        return aCell.value.toLowerCase() < bCell.value.toLowerCase() ? -1 : 1;
      },
      duration: (aCell, bCell) => {
        const aValue = moment.duration(aCell.duration).asMilliseconds();
        const bValue = moment.duration(bCell.duration).asMilliseconds();
        return aValue > bValue ? -1 : 1;
      },
    };
    const tableNonWorkingHeaderRowIndex = findIndex(table, { rowType: 'nonWorkingHeader' });
    const tableHeader = table.slice(0, 1);
    const tableWorkingOperators = table.slice(1, tableNonWorkingHeaderRowIndex);
    const tableNonWorkingHeader = table.slice(tableNonWorkingHeaderRowIndex, tableNonWorkingHeaderRowIndex + 1);
    const tableNonWorkingOperators = table.slice(tableNonWorkingHeaderRowIndex + 1, -1);
    const tableSummary = table.slice(-1);

    const multiplier = isAscending ? -1 : 1;
    const columnIndex = findIndex(tableSummary[0].cells, { columnName });
    const columnValueType = tableSummary[0].cells[columnIndex].valueType;

    const cellSorter = cellSorters[columnValueType];
    const tableSorter = (rowA, rowB) => {
      if (cellSorter) {
        return cellSorter(rowA.cells[columnIndex], rowB.cells[columnIndex]) * multiplier;
      }
      return 0;
    };
    return [
      ...tableHeader,
      ...tableWorkingOperators.sort(tableSorter),
      ...tableNonWorkingHeader,
      ...tableNonWorkingOperators.sort(tableSorter),
      ...tableSummary,
    ];
  };

  toggleSorting = (columnName) => {
    const { reportRatingTable, reportRatingSortBy, reportRatingSortingIsAscending } = this.state;
    if (columnName === reportRatingSortBy) {
      this.setState({
        reportRatingTable: this.getSortedRatingTable(reportRatingTable, columnName, !reportRatingSortingIsAscending),
        reportRatingSortingIsAscending: !reportRatingSortingIsAscending,
      });
    }
    else {
      this.setState({
        reportRatingTable: this.getSortedRatingTable(reportRatingTable, columnName, false),
        reportRatingSortingIsAscending: false,
        reportRatingSortBy: columnName,
      });
    }
  };

  generatePeriodFilterOptions = () => {
    const options = [];
    const numberOfMonths = 12;
    const today = moment();
    const todayFormatted = today.format('YYYY-MM-DD');
    options.push({
      title: 'Сегодня',
      value: this.makePeriodFilterValue(todayFormatted, todayFormatted),
    });

    const yesterday = today.clone().subtract(1, 'day');
    options.push({
      title: 'Вчера',
      value: this.makePeriodFilterValue(yesterday.format('YYYY-MM-DD'), yesterday.format('YYYY-MM-DD')),
    });

    const startOfWeekFormatted = today.startOf('week').format('YYYY-MM-DD');
    if (startOfWeekFormatted !== todayFormatted) {
      options.push({
        title: 'Эта неделя',
        value: this.makePeriodFilterValue(startOfWeekFormatted, todayFormatted),
      });
    }

    const previousWeek = today.subtract(1, 'week');
    options.push({
      title: 'Прошлая неделя',
      dividerBelow: true,
      value: this.makePeriodFilterValue(
        previousWeek.startOf('week').format('YYYY-MM-DD'),
        previousWeek.endOf('week').format('YYYY-MM-DD'),
      ),
    });

    let month = today.clone();

    for (let index = 0; index < numberOfMonths; index++) {
      const monthFrom = month.startOf('month').format('YYYY-MM-DD');
      const monthTo = !index ? todayFormatted : month.endOf('month').format('YYYY-MM-DD');
      options.push({
        title: upperFirst(withMomentLocale('ru', () => month.format('MMMM'))),
        value: this.makePeriodFilterValue(monthFrom, monthTo),
        dividerBelow: index === numberOfMonths - 1,
      });
      month.subtract(1, 'month');
    }
    options.push({
      title: today.format('YYYY'),
      dividerBelow: true,
      value: this.makePeriodFilterValue(today.startOf('year').format('YYYY-MM-DD'), todayFormatted),
    });
    options.push({
      title: 'Выбрать период',
      value: 'custom',
    });
    return options;
  };

  parsePeriodFilterValue = (value) => {
    return value.split('|');
  };

  makePeriodFilterValue = (from, to) => {
    return [from, to].join('|');
  };

  onPeriodFilterChange = (data) => {
    const { selectedOperators } = this.state;
    if (data.value === 'custom') {
      this.setState({ isPeriodModalOpen: true });
    }
    else {
      const [ startDate, endDate ] = this.parsePeriodFilterValue(data.value);
      this.setState({
        startDate,
        endDate,
        periodFilterTitle: data.title,
      });
      this.onRatingReportChange(startDate, endDate, selectedOperators);
    }
  };

  onOperatorFilterChange = (data) => {
    const { startDate, endDate, selectedOperators } = this.state;
    let updatedSelectedOperators;
    if (selectedOperators.includes(data.value)) {
      updatedSelectedOperators = selectedOperators.filter(operator => operator !== data.value);
    }
    else {
      updatedSelectedOperators = [ ...selectedOperators, data.value ];
    }
    this.setState({
      selectedOperators: updatedSelectedOperators,
      operatorFilterTitle: this.getOperatorFilterTitle(updatedSelectedOperators),
    });
    this.onRatingReportChange(startDate, endDate, updatedSelectedOperators);
  };

  onOperatorFilterReset = (e) => {
    const { startDate, endDate } = this.state;
    e.preventDefault();
    e.stopPropagation();

    this.setState({
      selectedOperators: [],
      operatorFilterTitle: this.OPERATOR_FILTER_DEFAULT_TITLE,
    });
    this.onRatingReportChange(startDate, endDate, []);
  };

  getOperatorFilterTitle = (selectedOperators) => {
    const { currentCompanyOperators } = this.props;
    if (selectedOperators?.length) {
      const operator = find(currentCompanyOperators, { id: selectedOperators[0] });
      let title = getUserFullName(operator);
      if (selectedOperators.length > 1) {
        title = title + ' и еще ' + inflect(selectedOperators.length - 1, ['оператор', 'оператора', 'операторов']);
      }
      return title;
    }
    else {
      return this.OPERATOR_FILTER_DEFAULT_TITLE;
    }
  };

  generateOperatorFilterOptions = (searchQuery) => {
    const { currentCompanyOperators } = this.props;
    const normalizedQueryArray = compact((searchQuery || '').toLowerCase().trim().split(/\s/));
    let operators = currentCompanyOperators;

    if (normalizedQueryArray.length) {
      operators = currentCompanyOperators.filter(operator => {
        return find(normalizedQueryArray, queryWord => {
          return (operator.firstName || '').toLowerCase().startsWith(queryWord)
            || (operator.lastName || '').toLowerCase().startsWith(queryWord);
        });
      });
    }
    return operators.map(operator => {
      return {
        title: getUserFullName(operator),
        value: operator.id,
      };
    });
  };

  onOperatorFilterSearch(searchQuery){
    this.setState({
      operatorFilterOptions: this.generateOperatorFilterOptions(searchQuery),
    });
  };

  onOperatorFilterSearchReset = () => {
    this.setState({
      operatorFilterSearchQuery: '',
      operatorFilterOptions: this.generateOperatorFilterOptions(null),
    });
  };

  onPeriodModalDateApply = () => {
    const { periodFilterModalStartDate, periodFilterModalEndDate, selectedOperators } = this.state;
    const isSameDate = moment(periodFilterModalStartDate).isSame(periodFilterModalEndDate, 'date');
    const isSameMonth = moment(periodFilterModalStartDate).isSame(periodFilterModalEndDate, 'month');
    const periodFrom = isSameMonth ? moment(periodFilterModalStartDate).get('date') : this.humanizeDate(periodFilterModalStartDate);
    const periodFilterTitle = isSameDate
      ? this.humanizeDate(periodFilterModalStartDate)
      : `С ${periodFrom} по ${this.humanizeDate(periodFilterModalEndDate)}`;

    this.setState({
      isPeriodModalOpen: false,
      startDate: periodFilterModalStartDate,
      endDate: periodFilterModalEndDate,
      periodFilterTitle,
    });
    this.onRatingReportChange(periodFilterModalStartDate, periodFilterModalEndDate, selectedOperators);
  };

  humanizeDate = (date) => {
    return withMomentLocale('ru', () => moment(date).format('D MMMM'));
  };

  isPrimaryColumnHeader = (cell) => {
    return cell.hasSecondaryColumn && cell.cellFormatting === 'header';
  };

  isSecondaryColumnHeader = (cell) => {
    return cell.isSecondaryColumnFor && cell.cellFormatting === 'header';
  };

  onManualSwitchOffClick = (cell, row) => {

    if (cell.value && cell.employee?.id) {

      const totalCount = row.cells.find(({ columnName }) => {
        return columnName === 'manualSwitchOffCount';
      })?.value;

      const totalDuration = row.cells.find(({ columnName }) => {
        return columnName === 'manualSwitchOffDuration';
      })?.value;

      this.setState({
        manualSwitchOffAnalyticsData: {
          operator: cell.employee,
          totalCount,
          totalDuration,
        },
      });
    }
  };

  render(){
    const { className, renderReportMenu } = this.props;
    const {
      selectedOperators,
      reportRatingTable,
      reportRatingSortBy,
      reportRatingSortingIsAscending,
      periodFilterTitle,
      periodFilterOptions,
      periodFilterModalStartDate,
      periodFilterModalEndDate,
      isPeriodModalOpen,
      operatorFilterSearchQuery,
      operatorFilterOptions,
      operatorFilterTitle,
      isLoading,
      startDate,
      endDate,
      manualSwitchOffAnalyticsData,
    } = this.state;

    return (
      <div className={cn({
        [className]: true,
        [styles.isLoading]: isLoading,
      })}>

        <div className={styles.filterContainer}>

          {renderReportMenu()}

          <Dropdown
            buttonAppearance={true}
            dontKeepSelectedOption={true}
            items={periodFilterOptions}
            onChange={this.onPeriodFilterChange}
            overrideClassNames={{
              container: styles.filterItem,
              button: styles.filterButton,
            }}>
            {periodFilterTitle}
          </Dropdown>

          <Dropdown
            buttonAppearance={true}
            items={operatorFilterOptions}
            dontKeepSelectedOption={true}
            dontCloseOnChange={true}
            onClick={() => requestAnimationFrame(() => {
              this.filterOperatorsSearchInput.current?.focus?.();
            })}
            onChange={this.onOperatorFilterChange}
            onClose={this.onOperatorFilterSearchReset}
            overrideClassNames={{
              container: styles.filterItem,
              list: styles.filterOperatorsList,
              button: styles.filterButton,
              buttonArrow: uiStyles.hidden,
            }}
            renderItem={item => (
              <span className={cn({[styles.filterOperatorsItemSelected]: selectedOperators.includes(item.value)})}>
                <i className={cn(styles.filterOperatorsItemTick, 'icon-system-case-closed')}/>
                {item.title}
              </span>
            )}
            renderListTop={() => (
              <input className={styles.filterOperatorsSearch}
                     ref={this.filterOperatorsSearchInput}
                     type="text"
                     placeholder="Поиск операторов"
                     value={operatorFilterSearchQuery}
                     onChange={(e) => {
                       this.setState({operatorFilterSearchQuery: e.target.value});
                       this.debouncedOnOperatorFilterSearch(e.target.value);
                     }}/>
            )}>
            {operatorFilterTitle}
            {operatorFilterTitle !== this.OPERATOR_FILTER_DEFAULT_TITLE && (
              <i className={cn(styles.filterOperatorsReset, 'icon-close-medium')} onClick={this.onOperatorFilterReset}/>
            )}
            {operatorFilterTitle === this.OPERATOR_FILTER_DEFAULT_TITLE && (
              <i className={cn(styles.filterOperatorsArrow, 'icon-chevron-down')}/>
            )}
          </Dropdown>

          <button
            className={cn({[styles.exportBtn]: true})}
            onClick={this.onExportExcelFile}
          >
            Export to Excel
          </button>
        </div>

        <table id="analyticsTable" className={styles.table}>
          <tbody>
          {reportRatingTable.map((row, i) => (
            <tr key={i} className={cn({
              [styles.tableRow]: true,
              [styles.tableRowHeader]: row.rowType === 'header',
              [styles.tableRowSummary]: row.rowType === 'summary',
              [styles.tableRowNonWorkingHeader]: row.rowType === 'nonWorkingHeader',
            })}>
              {row.cells.map((cell, j) => !this.isSecondaryColumnHeader(cell) && (
                <td
                  key={j}
                  data-column={cell.columnName}
                  colSpan={this.isPrimaryColumnHeader(cell) ? 2 : 1}
                  onClick={() => cell.onClick && cell.onClick(cell, row)}
                  className={cn({
                    [styles.tableCell]: true,
                    [styles.tableCellPercent]: cell.className === 'percent',
                    [styles.tableCellClickable]: cell.onClick && !row.rowType,
                    [styles.tableCellIsSecondaryColumn]: cell.isSecondaryColumnFor,
                    [styles.tableCellHasSecondaryColumn]: cell.hasSecondaryColumn,
                    [styles.tableCellHasNoDashWhenEmpty]: cell.cellFormatting === 'noDashWhenEmpty' || cell.isSecondaryColumnFor,
                    [styles.tableCellIsSortedBy]: cell.columnName === reportRatingSortBy || cell.isSecondaryColumnFor === reportRatingSortBy,
                    [styles.tableCellFormattingNumber]: cell.cellFormatting === 'number',
                  })}>
                  {cell.cellFormatting === 'header' && (
                    <span className={styles.tableValueSort} onClick={() => this.toggleSorting(cell.columnName)}>
                      {cell.title}
                      {cell.columnName === reportRatingSortBy && (reportRatingSortingIsAscending ? ' ↑' : ' ↓')}
                    </span>
                  )}
                  {cell.cellFormatting === 'text' && cell.value}
                  {cell.cellFormatting === 'number' && Boolean(cell.value) && formatNumber(cell.value)}

                  {cell.cellFormatting === 'cases' && Boolean(cell.value) && (
                    <span className={styles.tableValuePrimary}>
                      {inflect(cell.value, ['кейс', 'кейса', 'кейсов'])}
                    </span>
                  )}
                  {cell.cellFormatting === 'rooms' && Boolean(cell.value) && (
                    <span className={styles.tableValueSecondary}>
                      {inflect(cell.value, ['клиент', 'клиента', 'клиентов'])}
                    </span>
                  )}
                  {cell.cellFormatting === 'count' && Boolean(cell.value) && (
                    <span className={styles.tableValuePrimary}>
                      {inflect(cell.value, ['раз', 'раза', 'раз'])}
                    </span>
                  )}
                  {cell.cellFormatting === 'percent' && Boolean(cell.value) && (
                    <span className={styles.tableValuePrimary}>
                      {(cell.value)}%
                    </span>
                  )}
                  {(cell.cellFormatting === 'duration' || cell.cellFormatting === 'durationSecondary') && Boolean(cell.value) && (
                    <span className={cn({
                      [styles.tableValueSecondary]: cell.cellFormatting === 'durationSecondary',
                    })}>
                      {stringifyTimePeriod(moment.duration(cell.value).asMilliseconds(), {format: 'numeric'})}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
          </tbody>
        </table>

        {isPeriodModalOpen && (
          <Modal title="Выберите период"
                 onClose={() => this.setState({isPeriodModalOpen: false})}
                 verticallyCentered={true}>
            С
            <Datepicker
              overrideClassNames={{
                container: styles.periodDatePicker,
                calendar: styles.periodDatePickerCalendar,
              }}
              onChange={date => this.setState({periodFilterModalStartDate: moment(date).format('YYYY-MM-DD')})}
              availableTo={new Date()}
              defaultValue={periodFilterModalStartDate}>
              {this.humanizeDate(periodFilterModalStartDate)}
              <i className={cn(styles.periodDatePickerArrow, 'icon-chevron-down')}/>
            </Datepicker>
            по
            <Datepicker
              overrideClassNames={{
                container: styles.periodDatePicker,
                calendar: styles.periodDatePickerCalendar,
              }}
              onChange={date => this.setState({periodFilterModalEndDate: moment(date).format('YYYY-MM-DD')})}
              availableFrom={periodFilterModalStartDate}
              availableTo={new Date()}
              defaultValue={periodFilterModalEndDate}>
              {this.humanizeDate(periodFilterModalEndDate)}
              <i className={cn(styles.periodDatePickerArrow, 'icon-chevron-down')}/>
            </Datepicker>
            <div className={styles.periodModalButton}>
              <button className={uiStyles.buttonPrimary} onClick={this.onPeriodModalDateApply}>
                Применить
              </button>
            </div>
          </Modal>
        )}

        {Boolean(manualSwitchOffAnalyticsData) && (
          <AnalyticsManualSwitchOff
            className={styles.manualSwitchOffModal}
            periodFrom={startDate}
            periodTo={endDate}
            data={manualSwitchOffAnalyticsData}
            onClose={() => this.setState({manualSwitchOffAnalyticsData: null})}/>
        )}

      </div>
    );
  }
}
